import {clamp} from 'ramda';

export const PERCENTAGE_MULTIPLIER = 100;
export const SEGMENT_COLORS = ['#FD9696', '#FFC799', '#FFDE85', '#C9E5AC', '#A8D98D'];

const GRADIENT_BLEND_ZONE = 10;

/**
 * Builds the CSS `background` gradient used for the indicator circle border in the
 * AttractivenessBar. The bar is divided into equal-width colored segments. When the
 * indicator circle overlaps a segment boundary, the gradient blends the two adjacent
 * colors over a fixed-width zone centered on the boundary.
 *
 * @param barWidthPx - Rendered width of the underlying bar in pixels.
 * @param circleDiameterPx - Rendered diameter of the indicator circle in pixels.
 * @param percentage - Indicator position along the bar, in `[0, 100]`.
 * @returns A CSS `linear-gradient(...)` string.
 */
export function getBorderGradient(
  barWidthPx: number,
  circleDiameterPx: number,
  percentage: number
): string {
  const segmentCount = SEGMENT_COLORS.length;
  const segmentWidthPercent = PERCENTAGE_MULTIPLIER / segmentCount;

  if (barWidthPx === 0 || circleDiameterPx === 0) {
    const segmentIndex = clamp(0, segmentCount - 1, Math.floor(percentage / segmentWidthPercent));
    const color = SEGMENT_COLORS[segmentIndex];
    return `linear-gradient(${color}, ${color})`;
  }

  const circleRadiusPercent = (circleDiameterPx / 2 / barWidthPx) * PERCENTAGE_MULTIPLIER;

  const leftEdge = Math.max(0, percentage - circleRadiusPercent);
  const rightEdge = Math.min(PERCENTAGE_MULTIPLIER, percentage + circleRadiusPercent);

  const leftSegmentIndex = clamp(0, segmentCount - 1, Math.floor(leftEdge / segmentWidthPercent));
  const rightSegmentIndex = clamp(0, segmentCount - 1, Math.floor(rightEdge / segmentWidthPercent));

  if (leftSegmentIndex === rightSegmentIndex) {
    const color = SEGMENT_COLORS[leftSegmentIndex];
    return `linear-gradient(${color}, ${color})`;
  }

  const boundary = rightSegmentIndex * segmentWidthPercent;
  const totalSpan = rightEdge - leftEdge;
  const leftRatio = ((boundary - leftEdge) / totalSpan) * PERCENTAGE_MULTIPLIER;

  const blendStart = Math.max(0, leftRatio - GRADIENT_BLEND_ZONE);
  const blendEnd = Math.min(PERCENTAGE_MULTIPLIER, leftRatio + GRADIENT_BLEND_ZONE);

  const leftColor = SEGMENT_COLORS[leftSegmentIndex];
  const rightColor = SEGMENT_COLORS[rightSegmentIndex];

  return `linear-gradient(to right, ${leftColor} ${blendStart}%, ${rightColor} ${blendEnd}%)`;
}
